import { useEffect, useMemo, useState } from 'react';
import {
  SAFE_LEGACY_ROUTE,
  fetchUiSurfaceBootstrap,
  saveUiSurfacePreference,
} from './uiSurfaceBootstrap';
import {
  createBearerAuthorization,
  evaluateNewSurfaceDecision,
} from './UiSurfaceBoundary';
import { UIUX_THREE_TENANT_PILOT_RELEASE_ID } from './uiuxPilotRelease';
import './ui-surface-selector.css';

const initialState = Object.freeze({ status: 'loading', decision: null, message: '' });
// 2026-08-18: role='admin'（本家管理者、client_id紐付け済み）も切替可能に。
const USER_SURFACE_ROLES = new Set(['client_admin', 'staff', 'admin']);

export const canOfferNewSurface = (decision, expectedReleaseId) => {
  if (!decision?.choice?.available) return false;
  return evaluateNewSurfaceDecision({
    ...decision,
    effectiveSurface: 'new',
    safeFallback: false,
  }, expectedReleaseId).allowed;
};

export default function UiSurfaceSelector({
  authenticatedUser,
  token,
  currentSurface = 'legacy',
  expectedReleaseId = UIUX_THREE_TENANT_PILOT_RELEASE_ID,
  fetchImpl,
  timeoutMs = 5000,
  locationObject = typeof window === 'undefined' ? null : window.location,
  className = '',
}) {
  const user = useMemo(() => ({
    id: authenticatedUser?.id,
    role: authenticatedUser?.role,
  }), [authenticatedUser?.id, authenticatedUser?.role]);
  const [state, setState] = useState(initialState);

  useEffect(() => {
    let active = true;
    setState(initialState);
    if (!user.id || !USER_SURFACE_ROLES.has(user.role)) {
      setState({ status: 'hidden', decision: null, message: '' });
      return () => { active = false; };
    }
    fetchUiSurfaceBootstrap({
      authenticatedUser: user,
      authHeader: createBearerAuthorization(token),
      fetchImpl,
      timeoutMs,
    }).then((decision) => {
      if (!active) return;
      setState(canOfferNewSurface(decision, expectedReleaseId)
        ? { status: 'ready', decision, message: '' }
        : { status: 'hidden', decision, message: '' });
    }).catch(() => {
      if (active) setState({ status: 'hidden', decision: null, message: '' });
    });
    return () => { active = false; };
  }, [expectedReleaseId, fetchImpl, timeoutMs, token, user]);

  if (state.status === 'loading' || state.status === 'hidden') return null;

  const selectSurface = async () => {
    const nextPreference = currentSurface === 'new' ? 'legacy' : 'new';
    setState((current) => ({ ...current, status: 'saving', message: '' }));
    const decision = await saveUiSurfacePreference({
      preference: nextPreference,
      authenticatedUser: user,
      authHeader: createBearerAuthorization(token),
      fetchImpl,
      timeoutMs,
    });

    const newSurfaceApproved = nextPreference === 'new'
      && evaluateNewSurfaceDecision(decision, expectedReleaseId).allowed;
    const legacySurfaceApproved = nextPreference === 'legacy'
      && decision?.safeFallback !== true
      && decision?.effectiveSurface === 'legacy';
    if (!newSurfaceApproved && !legacySurfaceApproved) {
      setState((current) => ({
        ...current,
        status: 'ready',
        message: '画面の切り替えを確認できませんでした。現在の画面をそのまま利用できます。',
      }));
      return;
    }

    const destination = newSurfaceApproved ? decision.routes.new : SAFE_LEGACY_ROUTE;
    if (typeof locationObject?.assign !== 'function') {
      setState((current) => ({
        ...current,
        status: 'ready',
        message: '移動先を開けませんでした。ページを再読み込みして、もう一度お試しください。',
      }));
      return;
    }
    locationObject.assign(destination);
  };

  const label = currentSurface === 'new' ? '以前の画面に戻す' : '新しい画面を試す';
  return (
    <div className={className} data-ui-surface-selector="true">
      <button type="button" onClick={selectSurface} disabled={state.status === 'saving'}>
        {state.status === 'saving' ? '画面を切り替えています…' : label}
      </button>
      {state.message && <p role="alert">{state.message}</p>}
    </div>
  );
}
